import SeatmapEndpoints from "../../api/SeatmapEndpoints";
import { EventPackage } from "../../models/AssignedSeating";
import { SectionSeatStatus } from "../../models/Event";
import AuthStore from "../../stores/AuthStore";
import CartStore from "../../stores/CartStore";
import { SeatGeom } from "./canvas/seatGeometry";
import { computeSeatVisual, PaintContext } from "./canvas/seatPaint";
import {
    PACKAGE_SEAT_COLOR,
    promptStrandedSeats,
    StrandedSeatEntry,
    strandedSeatLabel,
} from "./strandedSeatPrompt";
import {
    buildSeatRowIndex,
    findRemovalStrand,
    SeatRowIndex,
    StrandableSeat,
} from "./strandedSeats";

/**
 * The stranded-seat guard for cart-side removals (the trash button in the
 * cart drawer and on the checkout page), where there is no seatmap on screen
 * to consult. The row layout is fetched on demand — cached, since a template
 * never changes — while seat availability is re-read every time so the check
 * doesn't run on a stale picture of the map.
 */

export type CartSeatSource =
    | { kind: "seatmap"; eventSeatmapId: number }
    | { kind: "package"; eventPackageId: number };

type RowLayout = {
    index: SeatRowIndex;
    eventPackage?: EventPackage;
};

const sourceKey = (source: CartSeatSource) =>
    source.kind === "seatmap"
        ? `seatmap-${source.eventSeatmapId}`
        : `package-${source.eventPackageId}`;

const layoutCache = new Map<string, Promise<RowLayout | null>>();

function loadRowLayout(source: CartSeatSource): Promise<RowLayout | null> {
    const key = sourceKey(source);
    const cached = layoutCache.get(key);
    if (cached) return cached;

    const pending = (async (): Promise<RowLayout | null> => {
        try {
            if (source.kind === "seatmap") {
                const layout = await SeatmapEndpoints.getEventSeatmapLayout(
                    source.eventSeatmapId,
                );
                return { index: buildSeatRowIndex(layout) };
            }
            const { layout, eventPackage } =
                await SeatmapEndpoints.getEventPackageLayout(
                    source.eventPackageId,
                );
            return {
                index: buildSeatRowIndex(layout, eventPackage),
                eventPackage,
            };
        } catch (err) {
            // Never block a removal on a lookup we couldn't make.
            layoutCache.delete(key);
            return null;
        }
    })();

    layoutCache.set(key, pending);
    return pending;
}

async function loadStatuses(
    source: CartSeatSource,
): Promise<Map<number, SectionSeatStatus> | null> {
    try {
        const statuses =
            source.kind === "seatmap"
                ? await SeatmapEndpoints.getEventSeatmapStatuses(
                      source.eventSeatmapId,
                  )
                : await SeatmapEndpoints.getEventPackageStatuses(
                      source.eventPackageId,
                  );
        return new Map(statuses.map((s) => [s.seatId, s]));
    } catch (err) {
        return null;
    }
}

function paintContextFor(
    cartStore: CartStore,
    source: CartSeatSource,
    statusById: Map<number, SectionSeatStatus>,
): PaintContext {
    return {
        cartSeatIds: new Set(
            source.kind === "seatmap"
                ? cartStore.seatmapItems
                      .filter(
                          (it) =>
                              it.eventSeatmap?.id === source.eventSeatmapId,
                      )
                      .map((it) => it.sectionSeat?.id)
                      .filter((id): id is number => id != null)
                : [],
        ),
        packageSeatIds: new Set(
            source.kind === "package"
                ? cartStore.packageItems
                      .filter(
                          (it) =>
                              it.eventPackage.id === source.eventPackageId &&
                              it.seatId != null,
                      )
                      .map((it) => it.seatId!)
                : [],
        ),
        holdGroupSeatIds: new Set<number>(),
        statusById,
        boxoffice: AuthStore.boxoffice,
    };
}

function cartSeatEntry(
    cartStore: CartStore,
    source: CartSeatSource,
    g: SeatGeom,
): StrandedSeatEntry {
    const packageItem =
        source.kind === "package"
            ? cartStore.packageItems.find(
                  (it) =>
                      it.eventPackage.id === source.eventPackageId &&
                      it.seatId === g.seat.id,
              )
            : undefined;
    const cartItem =
        source.kind === "seatmap"
            ? cartStore.seatmapItems.find(
                  (it) =>
                      it.eventSeatmap?.id === source.eventSeatmapId &&
                      it.sectionSeat?.id === g.seat.id,
              )
            : undefined;

    return {
        id: g.seat.id,
        label: strandedSeatLabel(g.seat),
        typeName:
            packageItem?.eventPackagePriceGroup.name ??
            cartItem?.department.name,
        price:
            packageItem?.price ??
            cartItem?.basePrice ??
            cartItem?.department.price ??
            null,
        color:
            g.priceGroup?.type === "seat"
                ? g.priceGroup.priceGroup?.color ?? PACKAGE_SEAT_COLOR
                : PACKAGE_SEAT_COLOR,
    };
}

/**
 * Takes a seat out of the cart, asking first when doing so would leave a seat
 * nobody else can buy. Falls through to a plain removal whenever the guard
 * doesn't apply or the map couldn't be read — a buyer must always be able to
 * empty their cart.
 */
export async function removeCartSeatWithStrandGuard(args: {
    cartStore: CartStore;
    source: CartSeatSource;
    seatId: number;
    /** Removes one seat from the cart; called for the seat and each extra. */
    remove: (seatId: number) => Promise<void>;
}): Promise<void> {
    const { cartStore, source, seatId, remove } = args;

    // Box-office staff place and pull single seats deliberately, and an SDA
    // allocation is already a deliberate subset of a row.
    if (AuthStore.boxoffice || cartStore.sdaCode) {
        await remove(seatId);
        return;
    }

    const [layout, statusById] = await Promise.all([
        loadRowLayout(source),
        loadStatuses(source),
    ]);
    const row = layout?.index.get(seatId);
    if (!row || !statusById) {
        await remove(seatId);
        return;
    }

    const context = paintContextFor(cartStore, source, statusById);
    const seats: StrandableSeat[] = [];
    row.forEach((g) => {
        const visual = computeSeatVisual(g, context);
        if (!visual) return;
        seats.push({
            geom: g,
            sellable: visual.interactivity === "full",
            inCart: visual.inCart,
        });
    });

    const picked = seats.find((s) => s.geom.seat.id === seatId);
    const { stranded, alsoRemove } = findRemovalStrand(
        seats,
        new Set([seatId]),
    );

    if (!picked || alsoRemove.length === 0) {
        await remove(seatId);
        return;
    }

    promptStrandedSeats({
        mode: "remove",
        picked: cartSeatEntry(cartStore, source, picked.geom),
        companions: alsoRemove.map((g) =>
            cartSeatEntry(cartStore, source, g),
        ),
        strandedCount: stranded.length,
        onConfirm: () => {
            void (async () => {
                try {
                    // Sequential: CartStore drops a second call while one is
                    // still in flight.
                    await remove(seatId);
                    for (const g of alsoRemove) {
                        await remove(g.seat.id);
                    }
                } catch (err) {}
            })();
        },
    });
}
